import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Baby, School, BookOpen, Utensils, PlaySquare, Laptop, HelpCircle, Palette } from 'lucide-react';
import { motion } from 'framer-motion';

const programs = [
  {
    title: 'Early Learners Program',
    ageRange: 'Ages 1-5 Years',
    schedule: 'Morning 7AM - 3PM',
    image: 'https://static.prod-images.emergentagent.com/jobs/9dbc40b3-470e-44c2-bbfd-5443f4c2d978/images/3eed6a63b61fe5f80c70ce0eb4588f4068a4c0eb4321982ce46a634bff75e5cf.png',
    icon: Baby,
    color: 'primary',
    features: [
      { icon: BookOpen, text: 'Preschool Curriculum' },
      { icon: Utensils, text: 'Healthy Meals & Snacks' },
      { icon: PlaySquare, text: 'Indoor & Outdoor Play' },
    ],
    description: 'Our early learners program focuses on foundational skills through play-based learning, helping young children develop social, emotional, and cognitive abilities in a nurturing environment.',
  },
  {
    title: 'After School Program',
    ageRange: 'Ages 6-10 Years',
    schedule: 'Afternoon 3PM - 8PM',
    image: 'https://static.prod-images.emergentagent.com/jobs/9dbc40b3-470e-44c2-bbfd-5443f4c2d978/images/61c7b921f75f748105e548e51c3a4ad685aca45cc7c46aa585cc99d418e62ed0.png',
    icon: School,
    color: 'secondary',
    features: [
      { icon: Laptop, text: 'Learning Activities' },
      { icon: HelpCircle, text: 'Homework Help' },
      { icon: Palette, text: 'Fun & Enriching Program' },
    ],
    description: 'Our after school program provides a structured yet fun environment where school-aged children can complete homework, engage in educational activities, and participate in enrichment programs.',
  },
];

const WaveDividerTop = () => (
  <div className="absolute -top-1 left-0 right-0 z-10">
    <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-[20px] md:h-[40px]" preserveAspectRatio="none">
      <path d="M0 20C360 0 720 40 1080 20C1260 10 1380 15 1440 20V0H0V20Z" fill="hsl(38 50% 97%)" />
    </svg>
  </div>
);

const WaveDividerBottom = () => (
  <div className="absolute -bottom-1 left-0 right-0 z-10">
    <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-[20px] md:h-[40px]" preserveAspectRatio="none">
      <path d="M0 20C360 40 720 0 1080 20C1260 30 1380 25 1440 20V40H0V20Z" fill="hsl(38 50% 97%)" />
    </svg>
  </div>
);

const ProgramsSection = () => {
  return (
    <section id="programs" className="py-12 md:py-28 relative bg-secondary/8" data-testid="programs-section">
      <WaveDividerTop />
      <WaveDividerBottom />

      <div className="container mx-auto px-4 relative z-20">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-8 md:mb-20 space-y-4"
        >
          <span className="inline-block bg-primary/12 text-primary text-sm font-bold px-5 py-2 rounded-full tracking-wide uppercase">
            Our Programs
          </span>
          <h2 className="font-fredoka font-bold text-4xl sm:text-5xl" data-testid="programs-heading">
            <span className="text-primary">Programs</span> <span className="text-secondary">for Every</span> <span className="text-accent">Age</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground">
            Specialized programs tailored to meet the developmental needs of children at different stages.
          </p>
        </motion.div>

        {/* Programs - Asymmetric Layout */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-10 max-w-6xl mx-auto items-start">
          {programs.map((program, index) => {
            const IconComp = program.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ y: -6 }}
                className={`bg-white rounded-[2.5rem] overflow-hidden shadow-elevated border border-border/30 group`}
                data-testid={`program-card-${index}`}
              >
                {/* Image */}
                <div className="relative h-64 md:h-72 overflow-hidden">
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    style={{ objectPosition: index === 0 ? '50% 45%' : 'center' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/80 via-transparent to-transparent" />
                  <div className={`absolute top-5 right-5 w-12 h-12 rounded-2xl bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-soft`}>
                    <IconComp className={`w-6 h-6 text-${program.color}`} />
                  </div>
                </div>

                {/* Content */}
                <div className="p-7 md:p-8 space-y-5">
                  <div>
                    <h3 className={`font-fredoka font-bold text-2xl md:text-3xl text-${program.color} mb-3`} data-testid={`program-title-${index}`}>
                      {program.title}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge className={`bg-${program.color}/10 text-${program.color} border-${program.color}/20 rounded-full px-3 py-1 text-xs font-bold`}>
                        {program.ageRange}
                      </Badge>
                      <Badge className={`bg-${program.color}/10 text-${program.color} border-${program.color}/20 rounded-full px-3 py-1 text-xs font-bold`}>
                        {program.schedule}
                      </Badge>
                    </div>
                    <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
                      {program.description}
                    </p>
                  </div>

                  {/* Features */}
                  <div className="space-y-3">
                    {program.features.map((feature, idx) => {
                      const FIcon = feature.icon;
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-xl bg-${program.color}/10 flex items-center justify-center flex-shrink-0`}>
                            <FIcon className={`w-4 h-4 text-${program.color}`} />
                          </div>
                          <span className="text-foreground/80 font-medium text-sm">{feature.text}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA */}
                  <Link to="/register">
                    <Button
                      data-testid={`program-enroll-btn-${index}`}
                      className={`w-full bg-${program.color} hover:bg-${program.color}/90 text-white rounded-full py-5 font-bold shadow-soft hover:shadow-elevated transition-all duration-300 hover:scale-[1.02] active:scale-95`}
                    >
                      Enroll in This Program
                    </Button>
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProgramsSection;
